<template>
  <Teleport to="body">
  <div class="webgl-dom-wrap">
    <canvas ref="canvas" class="webgl-dom" :class="{ 'is-ready': ready }" />
  </div>
  </Teleport>
</template>

<script setup>
import { createDomRenderer } from '../utils/domRenderer';
import { createDomScene } from '../utils/domScene';

const canvas = ref(null);
const ready = ref(false);
const { $Lenis } = useNuxtApp();
const config = Object.freeze({
  enabled: true,
  strength: 0.12,
  radius: 0.6,
  followLerp: 0.12,
  velocityLerp: 0.12,
  drag: 2.1,
  convex: 3.75,
  dent: 0,
  edgeHeight: 161,
  fan: 0.6,
  curvature: 1.5,
  sensitivity: 1,
  velocityBased: true,
});
const uploadConfig = () => {
  for (const key of ['radius', 'drag', 'convex', 'dent', 'edgeHeight', 'fan', 'curvature', 'sensitivity']) {
    gl.uniform1f(locations[key], config[key]);
  }
  gl.uniform1f(locations.strength, config.enabled ? config.strength : 0);
  gl.uniform1f(locations.velocityBased, config.velocityBased ? 1 : 0);
};
let gl, scene, renderer, headerRenderer, foregroundRenderer, program, texture, headerTexture, foregroundTexture, vertices, vao, frame, locations;
let lost = false;
let lastTime;
let observer;
let root;
let domDirty = true;
let fontVersion = 0;
let scrollTarget;
let previousScroll;
let previousScrollTarget;
let scrollSpeed = 0;
let pointerActive = false;
const mouse = { x: -1, y: -1, tx: -1, ty: -1, px: -1, py: -1, vx: 0, vy: 0 };
const lerp = (from, to, factor) => from + (to - from) * factor;

const vertexShader = `#version 300 es
in vec2 position;
out vec2 uv;
void main() {
  uv = position * 0.5 + 0.5;
  gl_Position = vec4(position, 0.0, 1.0);
}`;

const fragmentShader = `#version 300 es
precision highp float;
uniform sampler2D source;
uniform vec2 resolution;
uniform vec2 mouse;
uniform vec2 velocity;
uniform float scrollSpeed;
uniform float foregroundScrollSpeed;
uniform float modalActive;
uniform float strength;
uniform float radius;
uniform float drag;
uniform float convex;
uniform float dent;
uniform float edgeHeight;
uniform float fan;
uniform sampler2D headerSource;
uniform float foregroundVisible;
uniform sampler2D foregroundSource;
uniform float curvature;
uniform float sensitivity;
uniform float velocityBased;
in vec2 uv;
out vec4 color;
void main() {
  vec2 point = vec2(uv.x, 1.0 - uv.y);
  vec2 delta = point - mouse;
  float distance = length(delta * vec2(resolution.x / resolution.y, 1.0));
  float falloff = 1.0 - smoothstep(0.0, radius, distance);
  vec2 offset = velocity * falloff * drag * strength;
  float speed = mix(1.0, min(length(velocity) * sensitivity, 1.0), velocityBased);
  float normalizedDistance = min(distance / radius, 1.0);
  float dome = pow(max(0.0, 1.0 - normalizedDistance * normalizedDistance), curvature);
  float deformation = min(dome * convex * strength * speed, 0.85);
  offset += delta * deformation;
  vec2 mouseOffset = offset;
  offset = vec2(0.0);
  float band = min(edgeHeight / resolution.y, 0.45);
  float top = clamp(point.y / band, 0.0, 1.0);
  float bottom = clamp((1.0 - point.y) / band, 0.0, 1.0);
  float topStretch = top * pow(1.0 - top, 2.0);
  float bottomStretch = bottom * pow(1.0 - bottom, 2.0);
  // Anchor the viewport edges; magnification peaks there and rejoins the unwarped view smoothly.
  offset.y += (topStretch - bottomStretch) * band * min(dent * strength, 0.95);
  float edgeFalloff = pow(1.0 - top, 2.0) + pow(1.0 - bottom, 2.0);
  float fanSpeed = min(scrollSpeed, 1.0);
  float horizontalScale = 1.0 + min(fan, 0.6) * fanSpeed * strength * edgeFalloff;
  offset.x += (point.x - 0.5) * (1.0 - 1.0 / horizontalScale);
  vec2 foregroundOffset = offset;
  if (modalActive > 0.5) {
    float modalScale = 1.0 + min(fan, 0.6) * min(foregroundScrollSpeed, 1.0) * strength * edgeFalloff;
    foregroundOffset.x = (point.x - 0.5) * (1.0 - 1.0 / modalScale);
    offset = vec2(0.0);
  }
  offset += mouseOffset;
  vec2 sampleUv = clamp(point - offset, vec2(0.0), vec2(1.0));
  color = texture(source, vec2(sampleUv.x, 1.0 - sampleUv.y));
  vec2 headerUv = point - mouseOffset;
  if (headerUv.y >= 0.0 && headerUv.y <= 1.0 && headerUv.x >= 0.0 && headerUv.x <= 1.0) {
    vec4 headerColor = texture(headerSource, vec2(headerUv.x, 1.0 - headerUv.y));
    vec3 headerRgb = headerColor.rgb / max(headerColor.a, 0.00001);
    color.rgb = mix(color.rgb, abs(color.rgb - headerRgb), headerColor.a);
  }
  if (foregroundVisible > 0.5) {
    vec2 foregroundUv = clamp(point - foregroundOffset - mouseOffset, vec2(0.0), vec2(1.0));
    vec4 foregroundColor = texture(foregroundSource, vec2(foregroundUv.x, 1.0 - foregroundUv.y));
    color.rgb = foregroundColor.rgb + color.rgb * (1.0 - foregroundColor.a);
  }
}`;

const compile = (type, code) => {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, code);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const message = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(message);
  }
  return shader;
};

const resize = () => {
  const maxSize = gl.getParameter(gl.MAX_TEXTURE_SIZE);
  const dpr = Math.min(window.devicePixelRatio || 1, maxSize / innerWidth, maxSize / innerHeight);
  canvas.value.width = Math.round(innerWidth * dpr);
  canvas.value.height = Math.round(innerHeight * dpr);
  renderer.resize(innerWidth, innerHeight, dpr);
  headerRenderer.resize(innerWidth, innerHeight, dpr);
  foregroundRenderer.resize(innerWidth, innerHeight, dpr);
  domDirty = true;
  gl.viewport(0, 0, canvas.value.width, canvas.value.height);
  gl.useProgram(program);
  gl.uniform2f(locations.resolution, innerWidth, innerHeight);
};

const stop = () => {
  cancelAnimationFrame(frame);
  document.body.classList.remove('has-dom-canvas');
  ready.value = false;
};

const render = time => {
  if (lost) return;
  try {
    const animations = document.getAnimations().some(animation => {
      const target = animation.effect?.target;
      return animation.playState === 'running' && target instanceof Element
        && (root.contains(target) || target.closest('#header')) && !target.closest('#cursor');
    });
    if (domDirty || animations) {
      domDirty = false;
      const cache = {};
      renderer.update(cache, fontVersion);
      headerRenderer.update(cache, fontVersion);
      foregroundRenderer.update(cache, fontVersion);
      scrollTarget = document.querySelector('.about') || window;
    }
    renderer.draw();
    headerRenderer.draw();
    const foregroundVisible = foregroundRenderer.draw();
    const dt = Math.min(0.05, Math.max(1 / 240, lastTime === undefined ? 1 / 60 : (time - lastTime) / 1000));
    lastTime = time;
    // Lerp coefficients use 60 Hz as reference, independent of display refresh rate.
    const follow = 1 - Math.pow(1 - config.followLerp, dt * 60);
    const response = 1 - Math.pow(1 - config.velocityLerp, dt * 60);
    const scroll = scrollTarget === window ? ($Lenis?.core?.scroll ?? window.scrollY) : scrollTarget.scrollTop;
    const scrollDelta = previousScroll === undefined || previousScrollTarget !== scrollTarget ? 0 : scroll - previousScroll;
    const targetScrollSpeed = Math.min(Math.abs(scrollDelta) / dt / innerHeight, 1);
    scrollSpeed = lerp(scrollSpeed, targetScrollSpeed, response);
    previousScroll = scroll;
    previousScrollTarget = scrollTarget;
    const vx = pointerActive ? (mouse.tx - mouse.px) / dt / 12 : 0;
    const vy = pointerActive ? (mouse.ty - mouse.py) / dt / 12 : 0;
    mouse.vx = lerp(mouse.vx, vx, response);
    mouse.vy = lerp(mouse.vy, vy, response);
    mouse.x = lerp(mouse.x, mouse.tx, follow);
    mouse.y = lerp(mouse.y, mouse.ty, follow);
    mouse.px = mouse.tx;
    mouse.py = mouse.ty;
    gl.useProgram(program);
    gl.bindVertexArray(vao);
    gl.viewport(0, 0, canvas.value.width, canvas.value.height);
    for (const [unit, target] of [[gl.TEXTURE0, texture], [gl.TEXTURE1, headerTexture], [gl.TEXTURE2, foregroundTexture]]) {
      gl.activeTexture(unit);
      gl.bindTexture(gl.TEXTURE_2D, target);
    }
    gl.uniform1f(locations.foregroundVisible, foregroundVisible ? 1 : 0);
    gl.uniform2f(locations.mouse, mouse.x, mouse.y);
    gl.uniform2f(locations.velocity, mouse.vx, mouse.vy);
    gl.uniform1f(locations.scrollSpeed, scrollTarget === window ? scrollSpeed : 0);
    gl.uniform1f(locations.foregroundScrollSpeed, scrollTarget === window ? 0 : scrollSpeed);
    gl.uniform1f(locations.modalActive, scrollTarget === window ? 0 : 1);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    if (!ready.value) {
      document.body.classList.add('has-dom-canvas');
      ready.value = true;
    }
    frame = requestAnimationFrame(render);
  } catch (error) {
    console.warn('[webgl-dom]', error);
    stop();
  }
};

const onPointerMove = event => {
  if (!pointerActive) {
    mouse.x = event.clientX / innerWidth;
    mouse.y = event.clientY / innerHeight;
    mouse.px = mouse.x;
    mouse.py = mouse.y;
  }
  pointerActive = true;
  mouse.tx = event.clientX / innerWidth;
  mouse.ty = event.clientY / innerHeight;
};
const onPointerLeave = () => { pointerActive = false; };
const onVisibilityChange = () => {
  cancelAnimationFrame(frame);
  if (document.hidden || lost || !ready.value) return;
  lastTime = undefined;
  previousScroll = undefined;
  pointerActive = false;
  domDirty = true;
  frame = requestAnimationFrame(render);
};
const onContextLost = event => {
  event.preventDefault();
  lost = true;
  stop();
};
const invalidateDom = event => {
  const target = event?.target;
  if (target instanceof Element && target.closest('.webgl-dom-wrap, #cursor')) return;
  domDirty = true;
};
const onFontsLoaded = () => {
  fontVersion++;
  domDirty = true;
};

onMounted(() => {
  try {
    gl = canvas.value.getContext('webgl2', { alpha: false, antialias: false });
    if (!gl) { stop(); return; }
    const headerElement = document.querySelector('#header');
    const headerZIndex = parseFloat(getComputedStyle(headerElement).zIndex) || 0;
    root = document.querySelector('#__nuxt');
    scene = createDomScene(gl);
    renderer = createDomRenderer(root, { scene, invalidate: invalidateDom, maxZIndex: headerZIndex });
    headerRenderer = createDomRenderer(headerElement, { scene, invalidate: invalidateDom, transparentBackground: true });
    foregroundRenderer = createDomRenderer(root, {
      scene, invalidate: invalidateDom,
      transparentBackground: true,
      aboveZIndex: headerZIndex,
    });
    program = gl.createProgram();
    const shaders = [compile(gl.VERTEX_SHADER, vertexShader), compile(gl.FRAGMENT_SHADER, fragmentShader)];
    shaders.forEach(shader => gl.attachShader(program, shader));
    gl.linkProgram(program);
    shaders.forEach(shader => gl.deleteShader(shader));
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(program));
    gl.useProgram(program);
    vao = gl.createVertexArray();
    gl.bindVertexArray(vao);
    vertices = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, vertices);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
    const position = gl.getAttribLocation(program, 'position');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    locations = Object.fromEntries(['resolution', 'mouse', 'velocity', 'scrollSpeed', 'foregroundScrollSpeed', 'modalActive', 'foregroundVisible', 'strength', 'radius', 'drag', 'convex', 'dent', 'edgeHeight', 'fan', 'curvature', 'sensitivity', 'velocityBased'].map(name => [name, gl.getUniformLocation(program, name)]));
    texture = renderer.texture;
    headerTexture = headerRenderer.texture;
    foregroundTexture = foregroundRenderer.texture;
    gl.uniform1i(gl.getUniformLocation(program, 'source'), 0);
    gl.uniform1i(gl.getUniformLocation(program, 'headerSource'), 1);
    gl.uniform1i(gl.getUniformLocation(program, 'foregroundSource'), 2);
    uploadConfig();
    resize();
    observer = new MutationObserver(records => {
      if (records.some(record => {
        const target = record.target.nodeType === Node.ELEMENT_NODE ? record.target : record.target.parentElement;
        return !target?.closest('.webgl-dom-wrap, #cursor');
      })) domDirty = true;
    });
    observer.observe(document.documentElement, { subtree: true, childList: true, characterData: true, attributes: true });
    document.fonts.addEventListener('loadingdone', onFontsLoaded);
    for (const event of ['scroll', 'load', 'loadeddata', 'input', 'mouseover', 'mouseout', 'focusin', 'focusout', 'transitionend', 'animationend']) {
      document.addEventListener(event, invalidateDom, true);
    }
    window.addEventListener('resize', resize);
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    document.addEventListener('pointerleave', onPointerLeave);
    document.addEventListener('visibilitychange', onVisibilityChange);
    canvas.value.addEventListener('webglcontextlost', onContextLost);
    frame = requestAnimationFrame(render);
  } catch (error) {
    console.warn('[webgl-dom]', error);
    stop();
  }
});

onUnmounted(() => {
  stop();
  window.removeEventListener('resize', resize);
  window.removeEventListener('pointermove', onPointerMove);
  document.removeEventListener('pointerleave', onPointerLeave);
  document.removeEventListener('visibilitychange', onVisibilityChange);
  observer?.disconnect();
  document.fonts.removeEventListener('loadingdone', onFontsLoaded);
  for (const event of ['scroll', 'load', 'loadeddata', 'input', 'mouseover', 'mouseout', 'focusin', 'focusout', 'transitionend', 'animationend']) {
    document.removeEventListener(event, invalidateDom, true);
  }
  canvas.value?.removeEventListener('webglcontextlost', onContextLost);
  renderer?.destroy();
  headerRenderer?.destroy();
  foregroundRenderer?.destroy();
  scene?.destroy();
  if (gl) {
    gl.deleteBuffer(vertices);
    gl.deleteVertexArray(vao);
    gl.deleteProgram(program);
  }
});
</script>

<style scoped>
.webgl-dom { position: fixed; inset: 0; width: 100%; height: 100%; opacity: 0; pointer-events: none; z-index: 2147483640; }
.webgl-dom.is-ready { opacity: 1; }
</style>
